import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../../components/feature/LanguageSwitcher';
import { Link, useLocation } from 'react-router-dom';
import ProtectedLogo from '../../../components/feature/ProtectedLogo';

export default function Navbar() {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  
  // Detekce stránek kde má být navbar vždy bílý
  const isAlwaysWhite = !['/'].includes(location.pathname);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavigation = (path: string) => {
    if (typeof window.REACT_APP_NAVIGATE === 'function') {
      window.REACT_APP_NAVIGATE(path);
    } else {
      window.location.href = path;
    }
    setIsMobileMenuOpen(false);
  };

  const isActive = (path: string) => location.pathname === path;
  // Na mobilu vždy bílé pozadí, na desktopu podle scrollu
  const showWhiteBg = isScrolled || isAlwaysWhite;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-[9999] transition-all duration-300 ${
          showWhiteBg ? 'bg-white shadow-md' : 'lg:bg-transparent bg-white shadow-md'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`flex items-center justify-between transition-all duration-300 h-16 ${isScrolled ? 'lg:h-12' : 'lg:h-20'}`}>
            {/* Logo */}
            <Link to="/" className={`flex items-center group relative z-10 ${!isScrolled ? 'lg:self-start lg:mt-1' : ''}`}>
              <ProtectedLogo 
                src="https://static.readdy.ai/image/555c563fff780fe2630def631567923a/7657cf4116acd66083a1141f7d171df6.png" 
                alt="VyŽehlené logo" 
                className={`transition-all duration-300 group-hover:scale-105 drop-shadow-lg h-12 w-12 ${isScrolled ? 'lg:h-10 lg:w-10' : 'lg:h-48 lg:w-48'} ${!showWhiteBg ? 'lg:brightness-0 lg:invert' : ''}`}
                watermarkText="© VyŽehlené 2024"
              />
            </Link>

            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center gap-1">
              <button
                onClick={() => handleNavigation('/')}
                className={`px-5 py-2.5 rounded-lg font-medium text-base transition-all cursor-pointer ${
                  isActive('/') 
                    ? 'bg-teal-100 text-teal-700' 
                    : showWhiteBg 
                      ? 'text-gray-700 hover:bg-gray-100 hover:text-gray-900' 
                      : 'text-white/90 hover:bg-white/10 hover:text-white'
                }`}
              >
                {t('nav.home')}
              </button>
              <button
                onClick={() => handleNavigation('/sluzby')}
                className={`px-5 py-2.5 rounded-lg font-medium text-base transition-all cursor-pointer ${
                  isActive('/sluzby') 
                    ? 'bg-teal-100 text-teal-700' 
                    : showWhiteBg 
                      ? 'text-gray-700 hover:bg-gray-100 hover:text-gray-900' 
                      : 'text-white/90 hover:bg-white/10 hover:text-white'
                }`}
              >
                {t('nav.services')}
              </button>
              <button
                onClick={() => handleNavigation('/clenstvi')}
                className={`px-5 py-2.5 rounded-lg font-medium text-base transition-all cursor-pointer ${
                  isActive('/clenstvi') 
                    ? 'bg-teal-100 text-teal-700' 
                    : showWhiteBg 
                      ? 'text-gray-700 hover:bg-gray-100 hover:text-gray-900' 
                      : 'text-white/90 hover:bg-white/10 hover:text-white'
                }`}
              >
                {t('nav.membership')}
              </button>
              <button
                onClick={() => handleNavigation('/clenska-zona')}
                className={`px-5 py-2.5 rounded-lg font-medium text-base transition-all cursor-pointer flex items-center gap-2 ${
                  isActive('/clenska-zona') 
                    ? 'bg-teal-100 text-teal-700' 
                    : showWhiteBg 
                      ? 'text-gray-700 hover:bg-gray-100 hover:text-gray-900' 
                      : 'text-white/90 hover:bg-white/10 hover:text-white'
                }`}
              >
                <i className="ri-vip-crown-line text-lg"></i>
                {t('nav.memberZone')}
              </button>
              <button
                onClick={() => handleNavigation('/partner')}
                className={`px-5 py-2.5 rounded-lg font-medium text-base transition-all cursor-pointer ${
                  isActive('/partner') 
                    ? 'bg-teal-100 text-teal-700' 
                    : showWhiteBg 
                      ? 'text-gray-700 hover:bg-gray-100 hover:text-gray-900' 
                      : 'text-white/90 hover:bg-white/10 hover:text-white'
                }`}
              >
                {t('nav.partner')}
              </button>
            </div>

            {/* Right side actions */}
            <div className="hidden lg:flex items-center gap-4">
              <LanguageSwitcher isScrolled={showWhiteBg} />

              <button
                onClick={() => handleNavigation('/kosik')}
                className="bg-gradient-to-r from-teal-600 to-teal-500 hover:from-teal-700 hover:to-teal-600 text-white px-6 py-3 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap flex items-center gap-2 text-base"
              >
                <i className="ri-shopping-cart-2-line text-xl"></i>
                <span>Objednat</span>
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer z-10 text-gray-900 hover:bg-gray-100"
            >
              <i className={`text-2xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200 shadow-xl">
            <div className="px-4 py-4 space-y-2 max-h-[calc(100vh-4rem)] overflow-y-auto">
              <button
                onClick={() => handleNavigation('/')}
                className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg transition-colors cursor-pointer text-sm ${
                  isActive('/') 
                    ? 'bg-teal-100 text-teal-700 font-semibold' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <i className="ri-home-line text-lg w-5 h-5 flex items-center justify-center"></i>
                {t('nav.home')}
              </button>
              <button
                onClick={() => handleNavigation('/sluzby')}
                className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg transition-colors cursor-pointer text-sm ${
                  isActive('/sluzby') 
                    ? 'bg-teal-100 text-teal-700 font-semibold' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <i className="ri-shirt-line text-lg w-5 h-5 flex items-center justify-center"></i>
                {t('nav.services')}
              </button>
              <button
                onClick={() => handleNavigation('/clenstvi')}
                className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg transition-colors cursor-pointer text-sm ${
                  isActive('/clenstvi') 
                    ? 'bg-teal-100 text-teal-700 font-semibold' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <i className="ri-medal-line text-lg w-5 h-5 flex items-center justify-center"></i>
                {t('nav.membership')}
              </button>
              <button
                onClick={() => handleNavigation('/clenska-zona')}
                className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg transition-colors cursor-pointer text-sm ${
                  isActive('/clenska-zona') 
                    ? 'bg-teal-100 text-teal-700 font-semibold' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <i className="ri-vip-crown-line text-lg w-5 h-5 flex items-center justify-center"></i>
                {t('nav.memberZone')}
              </button>
              <button
                onClick={() => handleNavigation('/partner')}
                className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg transition-colors cursor-pointer text-sm ${
                  isActive('/partner') 
                    ? 'bg-teal-100 text-teal-700 font-semibold' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <i className="ri-handshake-line text-lg w-5 h-5 flex items-center justify-center"></i>
                {t('nav.partner')}
              </button>
              
              <div className="pt-2 border-t border-gray-200 mt-2">
                <button
                  onClick={() => handleNavigation('/kosik')}
                  className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-teal-600 to-teal-500 text-white px-4 py-3 rounded-lg font-semibold cursor-pointer text-sm"
                >
                  <i className="ri-shopping-cart-2-line text-lg"></i>
                  <span>Objednat nyní</span>
                </button>
              </div>
              
              <div className="pt-2">
                <LanguageSwitcher isScrolled={true} />
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}
